import { EventEmitter, Injectable, Output } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

import { NGXLogger } from "ngx-logger";
import { NavigationStart, Router } from "@angular/router";
import { AlertService } from "./alert.service";

@Injectable({
  providedIn: "root",
})
export class ValidationService {
  fieldErrors$: Observable<FieldError[]>;

  private fieldErrors: FieldError[] = [];

  // tslint:disable-next-line:variable-name
  private _fieldErrors: BehaviorSubject<FieldError[]>;

  @Output()
  public updated$ = new EventEmitter<FieldError>();

  constructor(private router: Router,                 
    private alertSvc: AlertService,
    private logger: NGXLogger) {
    this._fieldErrors = new BehaviorSubject<FieldError[]>([]);
    this.fieldErrors$ = this._fieldErrors.asObservable();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.clear();
        this.alertSvc.clear();
      }
    });
  }

  error(object: string, field: string, message: string): FieldError {
    return this.addField(FieldError.error(object + "." + field, message));
  }

  warning(object: string, field: string, message: string): FieldError {
    return this.addField(FieldError.warning(object + "." + field, message));
  }

  info(object: string, field: string, message: string): FieldError {
    return this.addField(FieldError.info(object + "." + field, message));
  }

  success(object: string, field: string, message: string): FieldError {
    return this.addField(FieldError.success(object + "." + field, message));
  }

  addField(error: FieldError) {
    this.logger.trace("alert - field", error);
    this.fieldErrors.push(error);
    const errors = Object.assign([], this.fieldErrors);
    // console.log('fielderrors', errors)
    this._fieldErrors.next(errors);
    this.updated$.emit(error);
    return error;
  }

  hasFieldError(name: string) {
    return this.fieldErrors.find((value) => {
      return value.name === name;
    });
  }

  remove(alert: FieldError) {
    const index = this.fieldErrors.indexOf(alert);
    if (index > -1) {
      this.fieldErrors.splice(index, 1);
      return;
    }
  }

  clear() {
    this.logger.trace("alert - clear");
    this.fieldErrors = [];
    this._fieldErrors.next([]);
  }
}

export class FieldError {
  name: string | undefined;
  message: string | undefined;
  type: AlertType | undefined;

  static success(name: string, message: string): FieldError {
    return FieldError.create(name, message, AlertType.SUCCESS);
  }

  static info(name: string, message: string): FieldError {
    return FieldError.create(name, message, AlertType.INFO);
  }

  static warning(name: string, message: string): FieldError {
    return FieldError.create(name, message, AlertType.WARNING);
  }

  static error(name: string, message: string): FieldError {
    return FieldError.create(name, message, AlertType.ERROR);
  }

  static create(name: string, message: string, type: AlertType): FieldError {
    const alert: FieldError = new FieldError();
    alert.message = message;
    alert.type = type;
    alert.name = name;
    return alert;
  }

  get typeAsString(): string {
    switch (this.type) {
      case AlertType.SUCCESS:
        return "success";
      case AlertType.INFO:
        return "info";
      case AlertType.WARNING:
        return "warning";
      case AlertType.ERROR:
        return "danger";
    }
    return "";
  }

  static typeFromString(value: string): AlertType {
    switch (value) {
      case "success":
        return AlertType.SUCCESS;
      case "warn":
      case "warning":
        return AlertType.WARNING;
      case "danger":
        return AlertType.ERROR;
      case "info":
        return AlertType.INFO;
      default:
        return AlertType.INFO;
    }
  }
}

export enum AlertType {
  SUCCESS = 1,
  INFO = 2,
  WARNING = 3,
  ERROR = 4,
}
